import { parseLocalDate } from './dates.js';

// ========================================
// REVENUE SERVICE - CENTRALIZED REVENUE MANAGEMENT
// ========================================

export const RevenueService = {
  // Invoice status definitions - SINGLE SOURCE OF TRUTH
  INVOICE_STATUSES: {
    PENDING: 'pending',    // Job completed, no invoice sent yet
    SENT: 'sent',         // Invoice sent to customer, payment pending
    PAID: 'paid',         // Invoice paid - ONLY THIS COUNTS AS REVENUE
    OVERDUE: 'overdue',   // Invoice sent but payment overdue
    CANCELLED: 'cancelled' // Invoice cancelled
  },

  // Revenue calculation methods - CONSISTENT ACROSS ALL COMPONENTS
  calculateRevenueMetrics(jobs, invoices = null) {
    const metrics = {
      totalPotentialRevenue: 0,  // All jobs with quoted prices
      actualRevenue: 0,          // ONLY paid invoices
      pendingRevenue: 0,         // Sent invoices not yet paid
      outstandingRevenue: 0,     // Pending + Overdue invoices
      revenueByStatus: {
        pending: 0,
        sent: 0,
        paid: 0,
        overdue: 0,
        cancelled: 0
      },
      invoiceBreakdown: {
        totalInvoices: 0,
        paidInvoices: 0,
        sentInvoices: 0,
        pendingInvoices: 0,
        overdueInvoices: 0,
        cancelledInvoices: 0
      },
      conversionRate: 0,         // Paid / Total potential
      averageJobValue: 0,
      lastRevenueDate: null
    };

    if (!jobs || jobs.length === 0) {
      return metrics;
    }

    // Process jobs to extract revenue data
    jobs.forEach(job => {
      const jobAmount = this.parseJobAmount(job.quotedPrice);

      if (jobAmount > 0) {
        metrics.totalPotentialRevenue += jobAmount;
      }

      // Process invoices for this job
      if (job.invoices && typeof job.invoices === 'object') {
        const jobInvoices = Object.values(job.invoices);

        jobInvoices.forEach(invoice => {
          if (!invoice || !invoice.status) return;

          const invoiceAmount = this.parseJobAmount(invoice.amount || invoice.total || job.quotedPrice);

          metrics.invoiceBreakdown.totalInvoices++;

          switch (invoice.status.toLowerCase()) {
            case this.INVOICE_STATUSES.PAID:
              metrics.actualRevenue += invoiceAmount;
              metrics.revenueByStatus.paid += invoiceAmount;
              metrics.invoiceBreakdown.paidInvoices++;

              // Track last revenue date
              const paidDate = invoice.datePaid || invoice.dateUpdated || invoice.dateSent;
              if (paidDate) {
                const paidDateObj = new Date(paidDate);
                if (!metrics.lastRevenueDate || paidDateObj > metrics.lastRevenueDate) {
                  metrics.lastRevenueDate = paidDateObj;
                }
              }
              break;

            case this.INVOICE_STATUSES.SENT:
              // SENT invoices go to pending revenue (recently sent, waiting for payment)
              metrics.pendingRevenue += invoiceAmount;
              metrics.revenueByStatus.sent += invoiceAmount;
              metrics.invoiceBreakdown.sentInvoices++;
              break;

            case this.INVOICE_STATUSES.OVERDUE:
              // OVERDUE invoices go to outstanding revenue (> 7 days unpaid)
              metrics.outstandingRevenue += invoiceAmount;
              metrics.revenueByStatus.overdue += invoiceAmount;
              metrics.invoiceBreakdown.overdueInvoices++;
              break;

            case this.INVOICE_STATUSES.PENDING:
              // PENDING invoices go to pending revenue (not sent yet)
              metrics.pendingRevenue += invoiceAmount;
              metrics.revenueByStatus.pending += invoiceAmount;
              metrics.invoiceBreakdown.pendingInvoices++;
              break;

            case this.INVOICE_STATUSES.CANCELLED:
              metrics.revenueByStatus.cancelled += invoiceAmount;
              metrics.invoiceBreakdown.cancelledInvoices++;
              break;
          }
        });
      }
      // If job is completed but has no invoices, count as pending
      else if (job.status === 'completed' && jobAmount > 0) {
        metrics.revenueByStatus.pending += jobAmount;
        metrics.invoiceBreakdown.pendingInvoices++;
      }
    });

    // Calculate derived metrics
    metrics.conversionRate = metrics.totalPotentialRevenue > 0
      ? (metrics.actualRevenue / metrics.totalPotentialRevenue) * 100
      : 0;

    metrics.averageJobValue = jobs.length > 0
      ? metrics.totalPotentialRevenue / jobs.length
      : 0;

    return metrics;
  },

  // Parse job amount consistently
  parseJobAmount(amount) {
    if (!amount) return 0;

    // Handle different amount formats
    const cleanAmount = amount.toString().replace(/[$,\s]/g, '');
    const parsed = parseFloat(cleanAmount);

    return isNaN(parsed) ? 0 : parsed;
  },

  // Format currency consistently
  formatCurrency(amount, options = {}) {
    const defaultOptions = {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    };

    return new Intl.NumberFormat('en-US', { ...defaultOptions, ...options }).format(amount || 0);
  },

  // Get status badge class consistently
  getStatusBadgeClass(status) {
    const statusClasses = {
      [this.INVOICE_STATUSES.PENDING]: 'badge alert-warning text-dark',
      [this.INVOICE_STATUSES.SENT]: 'badge alert-primary',
      [this.INVOICE_STATUSES.PAID]: 'badge alert-success',
      [this.INVOICE_STATUSES.OVERDUE]: 'badge alert-danger',
      [this.INVOICE_STATUSES.CANCELLED]: 'badge alert-secondary text-dark'
    };

    return statusClasses[status?.toLowerCase()] || 'badge bg-secondary';
  },

  // Get status label consistently
  getStatusLabel(status) {
    const statusLabels = {
      [this.INVOICE_STATUSES.PENDING]: 'Pending',
      [this.INVOICE_STATUSES.SENT]: 'Sent',
      [this.INVOICE_STATUSES.PAID]: 'Paid',
      [this.INVOICE_STATUSES.OVERDUE]: 'Overdue',
      [this.INVOICE_STATUSES.CANCELLED]: 'Cancelled'
    };

    return statusLabels[status?.toLowerCase()] || 'Unknown';
  },

  // Create standardized invoice data structure
  createStandardizedInvoice(job, userId, status = 'sent', invoiceId = null) {
    const amount = this.parseJobAmount(job.quotedPrice);
    const currentDate = new Date().toISOString().split('T')[0];
    const timestamp = Date.now();

    return {
      sentBy: userId,
      sentDate: timestamp,
      status: status,
      amount: amount,
      total: amount,
      invoiceNumber: invoiceId ? this.generateInvoiceNumber(job.id, invoiceId) : null,
      dateCreated: currentDate,
      datePaid: status === this.INVOICE_STATUSES.PAID ? currentDate : null,
      dateUpdated: currentDate,
      customerName: job.customerName || 'Unknown Customer',
      customerEmail: job.customerEmail || '',
      serviceType: job.serviceType || 'Service',
      serviceLocation: job.serviceLocation || '',
      // Additional tracking fields
      resendCount: 0,
      originalAmount: amount
    };
  },

  // Coerce any date-ish value to a LOCAL YYYY-MM-DD string, or null. Never throws.
  //
  // Two reasons this exists. First, new Date(undefined).toISOString() raises
  // RangeError, which crashed the invoice list for invoices holding neither
  // dateCreated nor a valid sentDate. Second, toISOString() yields the UTC
  // calendar date: an invoice sent at 9pm in Orlando converts to TOMORROW.
  // parseLocalDate reads date-only strings as local, so this must emit local to
  // match, or an epoch sentDate round-trips into a date a day ahead.
  toLocalDateString(value) {
    const date = parseLocalDate(value);
    if (!date) return null;
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  },

  // Generate consistent invoice number
  generateInvoiceNumber(jobId, invoiceId) {
    // Extract meaningful parts from IDs for shorter invoice numbers
    const jobSuffix = jobId.slice(-6);
    const invoiceSuffix = invoiceId === 'pending' ? 'PND' : invoiceId.slice(-4);
    const dateStr = new Date().toISOString().slice(2, 10).replace(/-/g, '');
    return `INV-${dateStr}-${jobSuffix}-${invoiceSuffix}`;
  },

  // Server-side now. A browser that can write invoice status can write "paid",
  // which would make a payment webhook advisory rather than authoritative.
  // See communications/lib/invoices.js.
  async createInvoice(job, userId, status = 'sent') {
    try {
      const call = firebase.functions().httpsCallable('createInvoice');
      const { data } = await call({ jobId: job.id, status });
      if (!data || !data.success) {
        return { success: false, error: 'The server declined to create the invoice.' };
      }
      return {
        success: true,
        invoiceId: data.invoiceId,
        invoiceData: data.invoice,
        wasDuplicate: data.wasDuplicate,
      };
    } catch (error) {
      console.error('createInvoice failed:', error);
      return { success: false, error: error.message };
    }
  },

  // Server-side, and the server decides which transitions are legal: a cancelled
  // invoice cannot quietly become paid, and a paid one is reversed only by a
  // refund.
  async updateInvoiceStatus(jobId, invoiceId, newStatus, additionalData = {}) {
    const call = firebase.functions().httpsCallable('setInvoiceStatus');
    const { data } = await call({
      jobId,
      invoiceId,
      status: newStatus,
      extra: additionalData && additionalData.note ? { note: additionalData.note } : undefined,
    });
    if (!data || !data.success) {
      throw new Error('The server declined the status change.');
    }
    return true;
  },

  // Migration script to backfill missing invoice data
  async migrateIncompleteInvoices() {
    try {
      console.log('🔧 Starting invoice data migration...');

      const jobsSnapshot = await firebase.database().ref('jobs').once('value');
      const jobs = jobsSnapshot.val();

      if (!jobs) {
        console.log('No jobs found to migrate');
        return { success: true, migratedCount: 0 };
      }

      let migratedCount = 0;
      const updates = {};

      for (const [jobId, job] of Object.entries(jobs)) {
        if (!job.invoices) continue;

        for (const [invoiceId, invoice] of Object.entries(job.invoices)) {
          let needsUpdate = false;
          const updatedInvoice = { ...invoice };

          // Check for missing required fields
          if (!invoice.amount && job.quotedPrice) {
            updatedInvoice.amount = this.parseJobAmount(job.quotedPrice);
            updatedInvoice.total = updatedInvoice.amount;
            needsUpdate = true;
          }

          if (!invoice.invoiceNumber) {
            updatedInvoice.invoiceNumber = this.generateInvoiceNumber(jobId, invoiceId);
            needsUpdate = true;
          }

          if (!invoice.dateCreated) {
            const createdDate =
              this.toLocalDateString(invoice.sentDate) ||
              new Date().toISOString().split('T')[0];
            updatedInvoice.dateCreated = createdDate;
            needsUpdate = true;
          }

          if (!invoice.customerName && job.customerName) {
            updatedInvoice.customerName = job.customerName;
            needsUpdate = true;
          }

          if (!invoice.customerEmail && job.customerEmail) {
            updatedInvoice.customerEmail = job.customerEmail;
            needsUpdate = true;
          }

          if (!invoice.serviceType && job.serviceType) {
            updatedInvoice.serviceType = job.serviceType;
            needsUpdate = true;
          }

          if (!invoice.serviceLocation && job.serviceLocation) {
            updatedInvoice.serviceLocation = job.serviceLocation;
            needsUpdate = true;
          }

          // For paid invoices, ensure datePaid exists
          if (invoice.status === 'paid' && !invoice.datePaid) {
            updatedInvoice.datePaid =
              invoice.dateUpdated ||
              this.toLocalDateString(invoice.sentDate) ||
              new Date().toISOString().split('T')[0];
            needsUpdate = true;
          }

          // Add missing tracking fields
          if (typeof invoice.resendCount === 'undefined') {
            updatedInvoice.resendCount = 0;
            needsUpdate = true;
          }

          if (!invoice.originalAmount && updatedInvoice.amount) {
            updatedInvoice.originalAmount = updatedInvoice.amount;
            needsUpdate = true;
          }

          if (needsUpdate) {
            updates[`jobs/${jobId}/invoices/${invoiceId}`] = updatedInvoice;
            migratedCount++;
            console.log(`🔧 Migrating invoice ${invoiceId} for job ${jobId}`);
          }
        }
      }

      if (migratedCount > 0) {
        console.log(`🔧 Applying ${migratedCount} invoice updates...`);
        await firebase.database().ref().update(updates);
        console.log(`✅ Successfully migrated ${migratedCount} invoices`);
      } else {
        console.log('✅ No invoices needed migration');
      }

      return { success: true, migratedCount };
    } catch (error) {
      console.error('❌ Error during invoice migration:', error);
      return { success: false, error: error.message, migratedCount: 0 };
    }
  },

  // Clean up phantom invoices and duplicates
  async cleanupPhantomInvoices() {
    try {
      console.log('🧹 Starting phantom invoice cleanup...');

      const jobsSnapshot = await firebase.database().ref('jobs').once('value');
      const jobs = jobsSnapshot.val();

      if (!jobs) {
        console.log('No jobs found to clean');
        return { success: true, cleanedCount: 0 };
      }

      let cleanedCount = 0;
      const updates = {};

      for (const [jobId, job] of Object.entries(jobs)) {
        if (!job.invoices) continue;

        const invoices = Object.entries(job.invoices);

        // Group invoices by status and find duplicates
        const statusGroups = {};
        for (const [invoiceId, invoice] of invoices) {
          const status = invoice.status || 'sent';
          if (!statusGroups[status]) statusGroups[status] = [];
          statusGroups[status].push([invoiceId, invoice]);
        }

        // Remove duplicate invoices for the same status (keep the latest)
        for (const [status, statusInvoices] of Object.entries(statusGroups)) {
          if (statusInvoices.length > 1) {
            // Sort by creation time (latest first)
            statusInvoices.sort(([, a], [, b]) => {
              const timeA = a.createdAt || a.sentDate || 0;
              const timeB = b.createdAt || b.sentDate || 0;
              return timeB - timeA;
            });

            // Keep the first (latest), remove the rest
            for (let i = 1; i < statusInvoices.length; i++) {
              const [invoiceId] = statusInvoices[i];
              updates[`jobs/${jobId}/invoices/${invoiceId}`] = null;
              cleanedCount++;
              console.log(`🗑️ Removing duplicate ${status} invoice ${invoiceId} from job ${jobId}`);
            }
          }
        }

        // Remove invoices with invalid data
        for (const [invoiceId, invoice] of invoices) {
          if (!invoice.status && !invoice.sentDate && !invoice.amount) {
            updates[`jobs/${jobId}/invoices/${invoiceId}`] = null;
            cleanedCount++;
            console.log(`🗑️ Removing invalid invoice ${invoiceId} from job ${jobId}`);
          }
        }
      }

      if (cleanedCount > 0) {
        console.log(`🧹 Removing ${cleanedCount} phantom/duplicate invoices...`);
        await firebase.database().ref().update(updates);
        console.log(`✅ Successfully cleaned ${cleanedCount} phantom invoices`);
      } else {
        console.log('✅ No phantom invoices found');
      }

      return { success: true, cleanedCount };
    } catch (error) {
      console.error('❌ Error during phantom cleanup:', error);
      return { success: false, error: error.message, cleanedCount: 0 };
    }
  },

  // Generate revenue report data
  generateRevenueReport(jobs, timeframe = 'monthly') {
    const metrics = this.calculateRevenueMetrics(jobs);
    const paidJobs = jobs.filter(job =>
      job.invoices && Object.values(job.invoices).some(inv =>
        inv.status === this.INVOICE_STATUSES.PAID
      )
    );

    // Group by timeframe
    const timeGroupedData = this.groupJobsByTimeframe(paidJobs, timeframe);

    return {
      summary: metrics,
      timeSeriesData: timeGroupedData,
      generatedAt: new Date().toISOString()
    };
  },

  // Group jobs by timeframe for charts
  groupJobsByTimeframe(jobs, timeframe) {
    const groups = {};

    jobs.forEach(job => {
      if (!job.invoices) return;

      Object.values(job.invoices).forEach(invoice => {
        if (invoice.status !== this.INVOICE_STATUSES.PAID) return;

        const paidDate = new Date(invoice.datePaid || invoice.dateUpdated || invoice.dateSent || job.dateSubmitted);
        let groupKey;

        switch (timeframe) {
          case 'daily':
            groupKey = paidDate.toISOString().split('T')[0];
            break;
          case 'weekly':
            const weekStart = new Date(paidDate);
            weekStart.setDate(paidDate.getDate() - paidDate.getDay());
            groupKey = weekStart.toISOString().split('T')[0];
            break;
          case 'monthly':
            groupKey = `${paidDate.getFullYear()}-${String(paidDate.getMonth() + 1).padStart(2, '0')}`;
            break;
          case 'yearly':
            groupKey = paidDate.getFullYear().toString();
            break;
          default:
            groupKey = paidDate.toISOString().split('T')[0];
        }

        if (!groups[groupKey]) {
          groups[groupKey] = {
            period: groupKey,
            revenue: 0,
            jobCount: 0,
            jobs: []
          };
        }

        const amount = this.parseJobAmount(invoice.amount || invoice.total || job.quotedPrice);
        groups[groupKey].revenue += amount;
        groups[groupKey].jobCount++;
        groups[groupKey].jobs.push({
          jobId: job.id,
          amount: amount,
          customer: job.customerName,
          service: job.serviceType,
          date: paidDate
        });
      });
    });

    // Convert to array and sort
    return Object.values(groups).sort((a, b) => a.period.localeCompare(b.period));
  }
};

// ========================================
// REVENUE MIXIN - FOR VUE COMPONENTS
// ========================================

export const RevenueMixin = {
  data() {
    return {
      revenueCache: new Map(),
      lastRevenueUpdate: null,
      isUpdatingInvoice: false, // Prevent concurrent updates
      updateQueue: [] // Queue for pending updates
    };
  },

  computed: {
    // Consistent revenue metrics across all components
    revenueMetrics() {
      const cacheKey = `metrics-${this.jobs?.length || 0}-${this.userJobs?.length || 0}-${this.lastRevenueUpdate}`;

      if (this.revenueCache.has(cacheKey)) {
        return this.revenueCache.get(cacheKey);
      }

      const jobs = this.jobs || this.userJobs || this.jobsArray || [];
      const metrics = RevenueService.calculateRevenueMetrics(jobs);

      this.revenueCache.set(cacheKey, metrics);
      return metrics;
    },

    // Formatted revenue values
    formattedRevenue() {
      return {
        actual: RevenueService.formatCurrency(this.revenueMetrics.actualRevenue),
        pending: RevenueService.formatCurrency(this.revenueMetrics.pendingRevenue),
        outstanding: RevenueService.formatCurrency(this.revenueMetrics.outstandingRevenue),
        total: RevenueService.formatCurrency(this.revenueMetrics.totalPotentialRevenue),
        average: RevenueService.formatCurrency(this.revenueMetrics.averageJobValue)
      };
    },

    // Revenue stats for dashboard cards
    revenueStats() {
      return {
        actualRevenue: this.revenueMetrics.actualRevenue,
        pendingRevenue: this.revenueMetrics.pendingRevenue,
        outstandingRevenue: this.revenueMetrics.outstandingRevenue,
        totalPotentialRevenue: this.revenueMetrics.totalPotentialRevenue,
        conversionRate: this.revenueMetrics.conversionRate,
        averageJobValue: this.revenueMetrics.averageJobValue,

        // Invoice counts
        totalInvoices: this.revenueMetrics.invoiceBreakdown.totalInvoices,
        paidInvoices: this.revenueMetrics.invoiceBreakdown.paidInvoices,
        sentInvoices: this.revenueMetrics.invoiceBreakdown.sentInvoices,
        pendingInvoices: this.revenueMetrics.invoiceBreakdown.pendingInvoices
      };
    }
  },

  methods: {
    // Consistent status badge method
    getInvoiceStatusBadge(status) {
      return RevenueService.getStatusBadgeClass(status);
    },

    // Consistent status label method
    getInvoiceStatusLabel(status) {
      return RevenueService.getStatusLabel(status);
    },

    // Consistent currency formatting
    formatRevenue(amount) {
      return RevenueService.formatCurrency(amount);
    },

    // Alias for backward compatibility with HTML templates
    formatCurrency(amount) {
      return RevenueService.formatCurrency(amount);
    },

    // Clear all caches and force refresh
    clearAllCaches() {
      this.revenueCache.clear();
      if (this.activityCache) this.activityCache.clear();
      this.lastRevenueUpdate = Date.now();
      console.log('🔄 All caches cleared for state refresh');
    },

    // Process queued updates
    async processUpdateQueue() {
      if (this.updateQueue.length === 0) return;

      console.log(`🔄 Processing ${this.updateQueue.length} queued updates`);
      const updates = [...this.updateQueue];
      this.updateQueue = [];

      for (const update of updates) {
        try {
          await update();
        } catch (error) {
          console.error('❌ Failed to process queued update:', error);
        }
      }
    },

    // Enhanced invoice status update with better state management
    async updateInvoiceStatus(jobId, invoiceId, newStatus, additionalData = {}) {
      // Prevent concurrent updates
      if (this.isUpdatingInvoice) {
        console.log('⏳ Update already in progress, queueing...');
        return new Promise((resolve) => {
          this.updateQueue.push(async () => {
            const result = await this.updateInvoiceStatus(jobId, invoiceId, newStatus, additionalData);
            resolve(result);
          });
        });
      }

      this.isUpdatingInvoice = true;
      console.log(`🔄 Updating invoice ${invoiceId} status to: ${newStatus}`);

      try {
        // Update database
        await RevenueService.updateInvoiceStatus(jobId, invoiceId, newStatus, additionalData);

        // Clear all caches immediately
        this.clearAllCaches();

        // Refresh data in all components
        const refreshPromises = [];

        // Refresh main component data
        if (this.fetchInvoices && typeof this.fetchInvoices === 'function') {
          refreshPromises.push(this.fetchInvoices());
        }
        if (this.fetchData && typeof this.fetchData === 'function') {
          refreshPromises.push(this.fetchData());
        }
        if (this.fetchUserData && typeof this.fetchUserData === 'function') {
          refreshPromises.push(this.fetchUserData());
        }

        // Wait for all refreshes to complete
        await Promise.all(refreshPromises);

        // Emit update event for other components
        if (this.$emit) {
          this.$emit('invoice-updated', { jobId, invoiceId, newStatus, additionalData });
        }

        // Process any queued updates
        setTimeout(() => this.processUpdateQueue(), 100);

        console.log(`✅ Invoice ${invoiceId} successfully updated to: ${newStatus}`);
        return true;

      } catch (error) {
        console.error('❌ Failed to update invoice status:', error);
        return false;
      } finally {
        this.isUpdatingInvoice = false;
      }
    },

    // Clear revenue cache when data changes
    clearRevenueCache() {
      this.revenueCache.clear();
      this.lastRevenueUpdate = Date.now();
    },

    // Generate revenue report
    generateRevenueReport(timeframe = 'monthly') {
      const jobs = this.jobs || this.userJobs || this.jobsArray || [];
      return RevenueService.generateRevenueReport(jobs, timeframe);
    }
  },

  // Lifecycle hooks
  watch: {
    // Watch for changes in jobs data
    jobs: {
      handler() {
        this.clearRevenueCache();
      },
      deep: true
    },

    userJobs: {
      handler() {
        this.clearRevenueCache();
      },
      deep: true
    },

    jobsArray: {
      handler() {
        this.clearRevenueCache();
      },
      deep: true
    }
  }
};

